import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LayoutDashboard, Package, RotateCcw, Inbox, Star, Users, Download, MessageSquare, CreditCard, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatUSD } from "@/lib/format";
import { BackButton } from "@/components/BackButton";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: Package },
  { key: "returns", label: "Returns & Refunds", icon: RotateCcw },
  { key: "leads", label: "Lead Inbox", icon: Inbox },
  { key: "heroes", label: "Hero Products", icon: Star },
  { key: "reviews", label: "Reviews", icon: MessageSquare },
  { key: "payments", label: "Payments", icon: CreditCard, ownerOnly: true },
  { key: "team", label: "Team", icon: Users, ownerOnly: true },
];
const STAGES = ["received", "pending", "shipped", "delivered"];

const Card = ({ children, className = "" }) => (
  <div className={`rounded-md border border-charcoal/10 bg-sand/40 p-6 ${className}`}>{children}</div>
);

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [ov, setOv] = useState(null);
  const [orders, setOrders] = useState([]);
  const [stageFilter, setStageFilter] = useState("");
  const [returns, setReturns] = useState([]);
  const [leads, setLeads] = useState(null);
  const [heroQ, setHeroQ] = useState("");
  const [heroResults, setHeroResults] = useState([]);
  const [partial, setPartial] = useState({});
  const [team, setTeam] = useState([]);
  const [member, setMember] = useState({ name: "", email: "", password: "", role: "staff_view" });
  const [reviews, setReviews] = useState([]);
  const [stripe, setStripe] = useState(null);
  const [purging, setPurging] = useState(false);

  const isOwner = user?.role === "admin";
  const canEdit = user?.role === "admin" || user?.role === "staff_edit";

  useEffect(() => {
    if (user === null) navigate("/auth");
    if (user && !["admin", "staff_edit", "staff_view"].includes(user.role)) navigate("/");
  }, [user, navigate]);

  const loadOverview = () => api.get("/admin/overview").then((r) => setOv(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadOrders = () => api.get("/admin/orders", { params: stageFilter ? { delivery_status: stageFilter } : {} }).then((r) => setOrders(r.data)).catch(() => {});
  const loadReturns = () => api.get("/admin/returns").then((r) => setReturns(r.data)).catch(() => {});
  const loadLeads = () => api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
  const searchHeroes = (q) => api.get("/admin/products", { params: q ? { q } : {} }).then((r) => setHeroResults(r.data)).catch(() => {});
  const loadTeam = () => api.get("/admin/team").then((r) => setTeam(r.data)).catch(() => {});
  const loadReviews = () => api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
  const loadStripe = () => api.get("/admin/stripe").then((r) => setStripe(r.data)).catch((e) => toast.error(formatApiError(e)));

  const purgeTestData = async () => {
    if (!window.confirm("Delete ALL test data (test orders, test customer/staff accounts, test leads, messages, tickets and reviews)? Real customer data is untouched. This cannot be undone.")) return;
    setPurging(true);
    try {
      const r = await api.post("/admin/purge-test-data");
      const d = Object.entries(r.data.deleted).map(([k, v]) => `${k}: ${v}`).join(", ");
      toast.success(`Test data cleared — ${d}`);
      loadOverview();
    } catch (e) { toast.error(formatApiError(e)); } finally { setPurging(false); }
  };

  useEffect(() => {
    if (!user || !["admin", "staff_edit", "staff_view"].includes(user.role)) return;
    if (tab === "overview") loadOverview();
    if (tab === "orders") loadOrders();
    if (tab === "returns") loadReturns();
    if (tab === "leads") loadLeads();
    if (tab === "heroes") searchHeroes(heroQ);
    if (tab === "team" && isOwner) loadTeam();
    if (tab === "reviews") loadReviews();
    if (tab === "payments" && isOwner) loadStripe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user, stageFilter]);

  if (!user || !["admin", "staff_edit", "staff_view"].includes(user.role)) return null;

  const setStage = async (orderId, stage) => {
    try {
      await api.patch(`/admin/orders/${orderId}`, { delivery_status: stage });
      toast.success(`${orderId} → ${stage}`);
      loadOrders();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const decide = async (rid, decision) => {
    try {
      await api.patch(`/admin/returns/${rid}`, { decision, partial_amount: Number(partial[rid] || 0) });
      toast.success(`Return ${rid}: ${decision.replace(/_/g, " ")} — customer emailed`);
      loadReturns();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleHero = async (slug, featured) => {
    try {
      await api.patch(`/admin/products/${slug}`, { featured });
      searchHeroes(heroQ);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addMember = async () => {
    try {
      await api.post("/admin/team", member);
      toast.success(`${member.name} added as ${member.role === "staff_edit" ? "editor" : "viewer"}`);
      setMember({ name: "", email: "", password: "", role: "staff_view" });
      loadTeam();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeMember = async (id, name) => {
    try {
      await api.delete(`/admin/team/${id}`);
      toast.success(`${name} removed`);
      loadTeam();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const maxMonth = ov ? Math.max(...ov.monthly.map((m) => m.revenue), 1) : 1;

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-bone pt-24">
      <div className="mx-auto max-w-[1500px] px-6 py-14 sm:px-10">
        <BackButton label="Back to store" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="mb-2 text-xs uppercase tracking-[0.3em] text-taupe">Shop ONline New York</p>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h1 className="font-serif text-4xl font-light tracking-tighter">Dashboard</h1>
            {isOwner && (
              <button data-testid="purge-test-data-button" onClick={purgeTestData} disabled={purging}
                className="flex items-center gap-2 rounded-full border border-moss/40 px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-moss transition-colors duration-300 hover:bg-moss hover:text-bone disabled:opacity-50">
                <Trash2 size={14} /> {purging ? "Clearing..." : "Clear test data"}
              </button>
            )}
          </div>
        </motion.div>

        <div className="mt-10 flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.filter((t) => !t.ownerOnly || isOwner).map((t) => (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                tab === t.key ? "border-charcoal bg-charcoal text-bone" : "border-charcoal/20 text-ash hover:text-charcoal"}`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-10">
          {tab === "overview" && ov && (
            <div data-testid="admin-overview">
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <Card><p className="text-xs uppercase tracking-[0.2em] text-ash">Monthly turnover</p><p data-testid="kpi-turnover" className="mt-2 font-serif text-3xl">{formatUSD(ov.turnover_this_month)}</p>
                  <a data-testid="export-orders-csv" href={`${API_BASE}/admin/export/orders`}
                    className="mt-3 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] text-taupe hover:text-charcoal">
                    <Download size={12} /> Export orders CSV
                  </a></Card>
                <Card><p className="text-xs uppercase tracking-[0.2em] text-ash">Total revenue</p><p data-testid="kpi-revenue" className="mt-2 font-serif text-3xl">{formatUSD(ov.total_revenue)}</p></Card>
                <Card><p className="text-xs uppercase tracking-[0.2em] text-ash">Paid orders</p><p data-testid="kpi-orders" className="mt-2 font-serif text-3xl">{ov.orders_paid}</p></Card>
                <Card><p className="text-xs uppercase tracking-[0.2em] text-ash">Open returns</p><p data-testid="kpi-returns" className="mt-2 font-serif text-3xl">{ov.open_returns}</p></Card>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
                {STAGES.map((s) => (
                  <Card key={s}><p className="text-xs uppercase tracking-[0.2em] text-ash">{s}</p><p data-testid={`kpi-stage-${s}`} className="mt-2 font-serif text-2xl">{ov.stage_counts[s] || 0}</p></Card>
                ))}
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Card>
                  <p className="mb-6 text-xs uppercase tracking-[0.2em] text-ash">Turnover — last 6 months</p>
                  <div className="flex h-44 items-end gap-4" data-testid="monthly-chart">
                    {ov.monthly.map((m) => (
                      <div key={m.month} className="flex flex-1 flex-col items-center gap-2">
                        <span className="text-xs text-ash">{m.revenue ? formatUSD(m.revenue) : "—"}</span>
                        <div className="w-full rounded-sm bg-charcoal/85" style={{ height: `${Math.max((m.revenue / maxMonth) * 100, 2)}%` }} />
                        <span className="text-[10px] uppercase tracking-widest text-ash">{m.month.slice(5)}/{m.month.slice(2, 4)}</span>
                      </div>
                    ))}
                  </div>
                </Card>
                <Card>
                  <p className="mb-6 text-xs uppercase tracking-[0.2em] text-ash">Best sellers by revenue</p>
                  <div className="space-y-3">
                    {ov.top_products.length === 0 && <p className="text-sm font-light text-ash">No sales yet.</p>}
                    {ov.top_products.map((p, i) => (
                      <div key={p.name} data-testid={`top-product-${i}`} className="flex items-center justify-between gap-4 border-b border-charcoal/10 pb-3 text-sm last:border-0">
                        <span className="truncate font-light">{i + 1}. {p.name}</span>
                        <span className="whitespace-nowrap text-ash">{p.qty} sold · {formatUSD(p.revenue)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 flex gap-6 border-t border-charcoal/10 pt-4 text-xs uppercase tracking-[0.15em] text-ash">
                    <span data-testid="kpi-leads">Leads: {ov.leads}</span>
                    <span data-testid="kpi-messages">Messages: {ov.unread_messages}</span>
                    <span data-testid="kpi-tickets">Tickets: {ov.open_tickets}</span>
                    <span data-testid="kpi-live">Live products: {ov.products_live}</span>
                  </div>
                </Card>
              </div>
            </div>
          )}

          {tab === "payments" && (
            <div data-testid="admin-payments">
              {!stripe ? (
                <p className="text-sm font-light text-ash">Loading your Stripe account…</p>
              ) : (
                <>
                  <div className="mb-6 flex items-center gap-3">
                    <span data-testid="stripe-mode-badge" className={`rounded-full px-4 py-1.5 text-[11px] uppercase tracking-[0.15em] ${stripe.mode === "live" ? "bg-charcoal text-bone" : "border border-charcoal/30 text-ash"}`}>
                      Stripe · {stripe.mode === "live" ? "Live mode — real money" : "Test mode"}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Card><p className="text-xs uppercase tracking-[0.2em] text-ash">Available balance</p><p data-testid="stripe-available" className="mt-2 font-serif text-3xl">{formatUSD(stripe.available)}</p><p className="mt-2 text-xs font-light text-ash">Ready to pay out to your bank</p></Card>
                    <Card><p className="text-xs uppercase tracking-[0.2em] text-ash">Pending balance</p><p data-testid="stripe-pending" className="mt-2 font-serif text-3xl">{formatUSD(stripe.pending)}</p><p className="mt-2 text-xs font-light text-ash">Recent charges still settling</p></Card>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <Card>
                      <p className="mb-4 text-xs uppercase tracking-[0.2em] text-ash">Recent charges</p>
                      <div className="space-y-3">
                        {stripe.charges.length === 0 && <p className="text-sm font-light text-ash">No charges yet — they'll appear here the moment a customer pays.</p>}
                        {stripe.charges.map((c) => (
                          <div key={c.id} data-testid={`stripe-charge-${c.id}`} className="flex items-center justify-between gap-4 border-b border-charcoal/10 pb-3 text-sm last:border-0">
                            <div className="min-w-0">
                              <p className="truncate font-light">{c.email || c.id}</p>
                              <p className="text-xs text-ash">{c.created}</p>
                            </div>
                            <div className="text-right">
                              <p className="whitespace-nowrap">{formatUSD(c.amount)}</p>
                              <p className={`text-[11px] uppercase tracking-widest ${c.refunded ? "text-moss" : "text-ash"}`}>{c.refunded ? "refunded" : c.status}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                    <Card>
                      <p className="mb-4 text-xs uppercase tracking-[0.2em] text-ash">Payouts to your bank</p>
                      <div className="space-y-3">
                        {stripe.payouts.length === 0 && <p className="text-sm font-light text-ash">No payouts yet — Stripe pays out automatically on your account's schedule (usually every 2 business days).</p>}
                        {stripe.payouts.map((p) => (
                          <div key={p.id} data-testid={`stripe-payout-${p.id}`} className="flex items-center justify-between gap-4 border-b border-charcoal/10 pb-3 text-sm last:border-0">
                            <span className="font-light">Arrives {p.arrival || "—"}</span>
                            <span className="whitespace-nowrap">{formatUSD(p.amount)} <span className="ml-2 text-[11px] uppercase tracking-widest text-ash">{p.status}</span></span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "orders" && (
            <div data-testid="admin-orders">
              <div className="mb-6 flex flex-wrap gap-2">
                <button data-testid="stage-filter-all" onClick={() => setStageFilter("")}
                  className={`rounded-full border px-4 py-2 text-xs uppercase tracking-[0.15em] ${!stageFilter ? "border-charcoal bg-charcoal text-bone" : "border-charcoal/20 text-ash"}`}>All</button>
                {STAGES.map((s) => (
                  <button key={s} data-testid={`stage-filter-${s}`} onClick={() => setStageFilter(s)}
                    className={`rounded-full border px-4 py-2 text-xs uppercase tracking-[0.15em] ${stageFilter === s ? "border-charcoal bg-charcoal text-bone" : "border-charcoal/20 text-ash"}`}>{s}</button>
                ))}
              </div>
              <div className="space-y-4">
                {orders.length === 0 && <p className="text-sm font-light text-ash">No orders here yet.</p>}
                {orders.map((o) => (
                  <Card key={o.order_id} data-testid={`admin-order-${o.order_id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <p className="font-serif text-lg">{o.order_id} <span className="text-sm text-ash">· {formatUSD(o.amount)} · {o.email}</span></p>
                        <p className="mt-1 text-xs font-light text-ash">
                          {o.items?.map((i) => `${i.name} ×${i.qty}`).join(", ")} · {o.shipping?.city} {o.shipping?.zip}
                          {o.cj_order_id ? ` · CJ ${o.cj_order_id}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.15em] ${o.payment_status === "paid" ? "bg-moss/10 text-charcoal" : "bg-sand text-ash"}`}>{o.payment_status}</span>
                        <select data-testid={`order-stage-${o.order_id}`} value={o.delivery_status || "received"}
                          onChange={(e) => setStage(o.order_id, e.target.value)}
                          disabled={!canEdit}
                          className="rounded-full border border-charcoal/20 bg-transparent px-4 py-2 text-xs uppercase tracking-[0.15em] outline-none disabled:opacity-50">
                          {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {tab === "returns" && (
            <div className="space-y-4" data-testid="admin-returns">
              {returns.length === 0 && <p className="text-sm font-light text-ash">No returns raised.</p>}
              {returns.map((r) => (
                <Card key={r.return_id} data-testid={`admin-return-${r.return_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="font-serif text-lg">{r.return_id} <span className="text-sm text-ash">· order {r.order_id} · {formatUSD(r.order_amount || 0)} · {r.customer}</span></p>
                      <p className="mt-1 text-sm font-light text-ash">"{r.reason}"</p>
                      <p className="mt-1 text-xs uppercase tracking-[0.15em] text-ash">status: {r.status.replace(/_/g, " ")} · refund: {r.refund_status.replace(/_/g, " ")}{r.partial_amount ? ` (${formatUSD(r.partial_amount)})` : ""}</p>
                    </div>
                    {r.status === "requested" && canEdit && (
                      <div className="flex flex-wrap items-center gap-2">
                        <button data-testid={`return-accept-${r.return_id}`} onClick={() => decide(r.return_id, "accepted")}
                          className="rounded-full bg-charcoal px-4 py-2 text-xs uppercase tracking-[0.15em] text-bone hover:bg-moss">Accept full</button>
                        <input data-testid={`return-partial-${r.return_id}`} placeholder="$ partial" value={partial[r.return_id] || ""}
                          onChange={(e) => setPartial((p) => ({ ...p, [r.return_id]: e.target.value }))}
                          className="w-24 rounded-full border border-charcoal/20 bg-transparent px-3 py-2 text-xs outline-none" />
                        <button data-testid={`return-partial-btn-${r.return_id}`} onClick={() => decide(r.return_id, "partially_accepted")}
                          className="rounded-full border border-charcoal/20 px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-charcoal hover:text-bone">Partial</button>
                        <button data-testid={`return-decline-${r.return_id}`} onClick={() => decide(r.return_id, "declined")}
                          className="rounded-full border border-moss/40 px-4 py-2 text-xs uppercase tracking-[0.15em] text-moss hover:bg-moss hover:text-bone">Decline</button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {tab === "leads" && leads && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="admin-leads">
              <Card>
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-xs uppercase tracking-[0.2em] text-ash">Expansion leads ({leads.expansion_leads.length})</p>
                  <a data-testid="export-leads-csv" href={`${API_BASE}/admin/export/leads`}
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-taupe hover:text-charcoal">
                    <Download size={12} /> CSV
                  </a>
                </div>
                <div className="space-y-3 text-sm font-light">
                  {leads.expansion_leads.length === 0 && <p className="text-ash">None yet.</p>}
                  {leads.expansion_leads.map((l) => (
                    <div key={l.email} data-testid={`lead-${l.email}`} className="flex justify-between border-b border-charcoal/10 pb-2 last:border-0">
                      <span>{l.email}</span><span className="text-ash">ZIP {l.zip}</span>
                    </div>
                  ))}
                </div>
              </Card>
              <Card>
                <p className="mb-4 text-xs uppercase tracking-[0.2em] text-ash">Contact messages ({leads.contact_messages.length})</p>
                <div className="space-y-3 text-sm font-light">
                  {leads.contact_messages.length === 0 && <p className="text-ash">None yet.</p>}
                  {leads.contact_messages.map((m, i) => (
                    <div key={i} className="border-b border-charcoal/10 pb-2 last:border-0">
                      <p>{m.name} · <span className="text-ash">{m.email}</span></p>
                      <p className="mt-1 text-ash">{m.message}</p>
                    </div>
                  ))}
                </div>
              </Card>
              <Card>
                <p className="mb-4 text-xs uppercase tracking-[0.2em] text-ash">Support tickets ({leads.support_tickets.length})</p>
                <div className="space-y-3 text-sm font-light">
                  {leads.support_tickets.length === 0 && <p className="text-ash">None yet.</p>}
                  {leads.support_tickets.map((t) => (
                    <div key={t.ticket_id} data-testid={`admin-ticket-${t.ticket_id}`} className="border-b border-charcoal/10 pb-2 last:border-0">
                      <p>{t.subject} <span className="text-xs uppercase text-ash">· {t.status}</span></p>
                      <p className="mt-1 text-ash">{t.message}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {tab === "team" && isOwner && (
            <div data-testid="admin-team" className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <Card>
                <p className="mb-2 text-xs uppercase tracking-[0.2em] text-ash">Add a team member</p>
                <p className="mb-6 text-sm font-light text-ash">
                  Viewers (accountant, CA) see everything but can't change anything. Editors can move orders, decide returns and manage hero products.
                </p>
                <div className="space-y-4">
                  <input data-testid="member-name" placeholder="Full name" value={member.name}
                    onChange={(e) => setMember((f) => ({ ...f, name: e.target.value }))}
                    className="w-full rounded-md border border-charcoal/20 bg-transparent px-4 py-3 text-sm outline-none placeholder:text-ash/60 focus:border-charcoal" />
                  <input data-testid="member-email" type="email" placeholder="Email" value={member.email}
                    onChange={(e) => setMember((f) => ({ ...f, email: e.target.value }))}
                    className="w-full rounded-md border border-charcoal/20 bg-transparent px-4 py-3 text-sm outline-none placeholder:text-ash/60 focus:border-charcoal" />
                  <input data-testid="member-password" type="password" placeholder="Temporary password — 8+ characters" value={member.password}
                    onChange={(e) => setMember((f) => ({ ...f, password: e.target.value }))}
                    className="w-full rounded-md border border-charcoal/20 bg-transparent px-4 py-3 text-sm outline-none placeholder:text-ash/60 focus:border-charcoal" />
                  <select data-testid="member-role" value={member.role}
                    onChange={(e) => setMember((f) => ({ ...f, role: e.target.value }))}
                    className="w-full rounded-md border border-charcoal/20 bg-transparent px-4 py-3 text-sm outline-none">
                    <option value="staff_view">Viewer — read only</option>
                    <option value="staff_edit">Editor — view & edit</option>
                  </select>
                  <button data-testid="member-add" onClick={addMember}
                    disabled={!member.name || !member.email || !member.password}
                    className="rounded-full bg-charcoal px-8 py-4 text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-moss disabled:opacity-60">
                    Add member
                  </button>
                </div>
              </Card>
              <Card>
                <p className="mb-6 text-xs uppercase tracking-[0.2em] text-ash">Current team</p>
                <div className="space-y-3">
                  {team.map((m) => (
                    <div key={m.user_id} data-testid={`team-row-${m.user_id}`} className="flex items-center justify-between border-b border-charcoal/10 pb-3 text-sm last:border-0">
                      <div>
                        <p>{m.name} {m.role === "admin" && <span className="text-xs text-taupe">(you)</span>}</p>
                        <p className="text-xs font-light text-ash">{m.email}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="rounded-full bg-sand px-3 py-1 text-[11px] uppercase tracking-[0.15em] text-ash">
                          {m.role === "admin" ? "Owner" : m.role === "staff_edit" ? "Editor" : "Viewer"}
                        </span>
                        {m.role !== "admin" && (
                          <button data-testid={`team-remove-${m.user_id}`} onClick={() => removeMember(m.user_id, m.name)}
                            className="text-xs uppercase tracking-[0.15em] text-moss hover:text-charcoal">Remove</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {tab === "reviews" && (
            <div className="space-y-4" data-testid="admin-reviews">
              {reviews.length === 0 && <p className="text-sm font-light text-ash">No reviews yet.</p>}
              {reviews.map((r) => (
                <Card key={r.product_slug + r.user_id} data-testid={`admin-review-${r.product_slug}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="font-serif text-lg">{r.product_name} <span className="text-sm text-taupe">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span></p>
                      <p className="mt-1 text-sm font-light text-ash">{r.name} · {r.comment || "(no comment)"}</p>
                      {r.hidden && <p className="mt-1 text-xs uppercase tracking-[0.15em] text-moss">hidden from storefront</p>}
                    </div>
                    {canEdit && (
                      <button data-testid={`review-toggle-${r.user_id}`}
                        onClick={async () => {
                          try {
                            await api.patch("/admin/reviews", { product_slug: r.product_slug, user_id: r.user_id, hidden: !r.hidden });
                            loadReviews();
                          } catch (e) { toast.error(formatApiError(e)); }
                        }}
                        className={`rounded-full px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                          r.hidden ? "bg-charcoal text-bone hover:bg-moss" : "border border-charcoal/20 text-ash hover:text-charcoal"}`}>
                        {r.hidden ? "Show again" : "Hide"}
                      </button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {tab === "heroes" && (
            <div data-testid="admin-heroes">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
                <p className="max-w-lg text-sm font-light text-ash">
                  The six products marked as heroes appear in the home page "featured" section. Search your catalog and toggle.
                </p>
                <div className="text-right">
                  <button data-testid="sync-now-button"
                    onClick={async () => {
                      try {
                        toast.info("Re-syncing your CJ catalog — this takes a few minutes…");
                        const r = await api.post("/admin/sync-now");
                        toast.success(`Sync done: ${r.data.total_in_cj} in CJ, ${r.data.new} new, ${r.data.hidden_delisted} delisted`);
                      } catch (e) { toast.error(formatApiError(e)); }
                    }}
                    className="rounded-full border border-charcoal/20 px-6 py-3 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-charcoal hover:text-bone">
                    Re-sync catalog now
                  </button>
                  {ov?.last_refresh && (
                    <p data-testid="last-refresh" className="mt-2 text-xs font-light text-ash">
                      Last auto-refresh: {ov.last_refresh.ran_at?.slice(0, 10)} — weekly, automatic
                    </p>
                  )}
                </div>
              </div>
              <input data-testid="hero-search" placeholder="Search products…" value={heroQ}
                onChange={(e) => { setHeroQ(e.target.value); searchHeroes(e.target.value); }}
                className="mb-6 w-full max-w-md rounded-md border border-charcoal/20 bg-transparent px-4 py-3 text-sm outline-none placeholder:text-ash/60 focus:border-charcoal" />
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {heroResults.map((p) => (
                  <div key={p.slug} data-testid={`hero-row-${p.slug}`} className="flex items-center justify-between gap-4 rounded-md border border-charcoal/10 p-4">
                    <div className="flex items-center gap-4">
                      <img src={p.image} alt="" className="h-12 w-12 rounded-sm object-cover" />
                      <div>
                        <p className="text-sm">{p.name}</p>
                        <p className="text-xs text-ash">{p.category} · {formatUSD(p.price)}</p>
                      </div>
                    </div>
                    <button data-testid={`hero-toggle-${p.slug}`} onClick={() => toggleHero(p.slug, !p.featured)}
                      disabled={!canEdit}
                      className={`rounded-full px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-300 disabled:opacity-50 ${
                        p.featured ? "bg-charcoal text-bone" : "border border-charcoal/20 text-ash hover:text-charcoal"}`}>
                      {p.featured ? "Hero ✓" : "Set hero"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
